/**
 * Tool Configuration Component
 * View and manage AI agent tools (RAG, Calendar, CRM, Handoff, WhatsApp)
 */

import React, { useState, useEffect } from 'react';
import { orchestratorApi } from '../api/orchestrator';
import './GoogleCalendarAuth.css'; // Reuse styles

type ToolStatus = 'available' | 'unavailable' | 'rate_limited' | 'disabled';
type ToolCategory = 'knowledge' | 'booking' | 'crm' | 'communication' | 'whatsapp' | 'custom';

interface ToolDefinition {
  id: string;
  name: string;
  description: string;
  category: ToolCategory;
  handler: string;
  status: ToolStatus;
  parameters: ToolParameter[];
  rate_limit?: {
    max_calls_per_minute?: number;
    max_calls_per_hour?: number;
    max_calls_per_day?: number;
  };
  cost_per_call_usd?: number;
  version: string;
  tags: string[];
}

interface AvailabilitySetting {
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_enabled: boolean;
}

interface Entity {
  id: string;
  name: string;
  phone: string;
  is_active: boolean;
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Tool Gateway tool structure
interface ToolGatewayTool {
  name: string;
  description: string;
  required_permissions: string[];
  schema: {
    type: string;
    required?: string[];
    properties: Record<string, {
      type?: string;
      format?: string;
      minLength?: number;
      maxLength?: number;
      description?: string;
    }>;
  };
}

interface ToolParameter {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'object' | 'array';
  required: boolean;
  description: string;
  default?: any;
}

interface ToolExecution {
  id: string;
  tenant_id: string;
  entity_id: string;
  tool_id: string;
  agent_id?: string;
  conversation_id?: string;
  parameters: Record<string, any>;
  success: boolean;
  error_message?: string;
  execution_time_ms: number;
  cost_usd: number;
  metadata?: Record<string, any>;
  created_at: string;
}

interface ToolStats {
  total_calls: number;
  success_rate: number;
  avg_execution_time_ms: number;
  cost_usd: number;
}

const CATEGORY_COLORS: Record<ToolCategory, string> = {
  knowledge: '#3b82f6',
  booking: '#10b981',
  crm: '#8b5cf6',
  communication: '#f59e0b',
  whatsapp: '#25D366',
  custom: '#6b7280',
};

const STATUS_LABELS: Record<ToolStatus, string> = {
  available: 'Available',
  unavailable: 'Unavailable',
  rate_limited: 'Rate Limited',
  disabled: 'Disabled',
};

const STATUS_COLORS: Record<ToolStatus, string> = {
  available: '#10b981',
  unavailable: '#ef4444',
  rate_limited: '#f59e0b',
  disabled: '#6b7280',
};

interface ToolConfigurationProps {
  selectedEntityId: string | null;
}

export const ToolConfiguration: React.FC<ToolConfigurationProps> = ({ selectedEntityId }) => {

  // State
  const [tools, setTools] = useState<ToolDefinition[]>([]);
  const [stats, setStats] = useState<Record<string, ToolStats>>({});
  const [executions, setExecutions] = useState<ToolExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'tools' | 'history' | 'availability'>('tools');
  const [selectedTool, setSelectedTool] = useState<ToolDefinition | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info', text: string } | null>(null);

  // Availability settings state
  const [availability, setAvailability] = useState<AvailabilitySetting[]>([]);
  const [savingAvailability, setSavingAvailability] = useState(false);
  const [availabilityEntityId, setAvailabilityEntityId] = useState<string>('');
  const [entities, setEntities] = useState<Entity[]>([]);

  // Pagination for executions
  const [page, setPage] = useState(0);
  const [limit] = useState(20);

  // Load entities on mount
  useEffect(() => {
    loadEntities();
  }, []);

  // Initialize availability entity ID from selectedEntityId prop
  useEffect(() => {
    if (activeTab === 'availability' && selectedEntityId && !availabilityEntityId) {
      setAvailabilityEntityId(selectedEntityId);
    }
  }, [activeTab, selectedEntityId]);

  useEffect(() => {
    loadTools();
    loadStats();
    loadExecutions();
    if (activeTab === 'availability' && availabilityEntityId) {
      loadAvailability();
    }
  }, [page, selectedTool, activeTab, availabilityEntityId]);

  const loadTools = async () => {
    setLoading(true);
    try {
      // Fetch from orchestrator (legacy tools)
      const orchestratorResponse = await fetch('/api/tools', {
        headers: await orchestratorApi.getAuthHeaders()
      });

      const orchestratorData = await orchestratorResponse.json();
      const orchestratorTools: ToolDefinition[] = orchestratorData.ok ? orchestratorData.tools : [];

      // Fetch from tool gateway (new tools)
      let gatewayTools: ToolDefinition[] = [];
      try {
        const gatewayResponse = await fetch('/api/tools/gateway', {
          headers: await orchestratorApi.getAuthHeaders()
        });
        const gatewayData = await gatewayResponse.json();

        if (gatewayData.tools) {
          gatewayTools = gatewayData.tools.map((tool: ToolGatewayTool) => {
            // Determine category based on tool name
            let category: ToolCategory = 'custom';
            if (tool.name.startsWith('whatsapp.')) {
              category = 'whatsapp';
            } else if (tool.name.startsWith('calendar.')) {
              category = 'booking';
            }

            // Convert schema properties to ToolParameter[]
            const parameters: ToolParameter[] = Object.entries(tool.schema.properties || {}).map(([name, prop]: [string, any]) => ({
              name,
              type: prop.type || 'string',
              required: tool.schema.required?.includes(name) || false,
              description: prop.description || '',
            }));

            return {
              id: tool.name,
              name: tool.name,
              description: tool.description,
              category,
              handler: 'tool-gateway',
              status: 'available' as ToolStatus,
              parameters,
              version: '1.0.0',
              tags: tool.required_permissions || [],
            };
          });
        }
      } catch (gatewayError) {
        console.warn('Failed to load tools from tool gateway:', gatewayError);
      }

      // Merge tools from both sources, prioritizing orchestrator tools for duplicates
      const mergedTools = [...orchestratorTools];
      const orchestratorToolIds = new Set(orchestratorTools.map(t => t.id));

      for (const gatewayTool of gatewayTools) {
        if (!orchestratorToolIds.has(gatewayTool.id)) {
          mergedTools.push(gatewayTool);
        }
      }

      setTools(mergedTools);
    } catch (error) {
      console.error('Failed to load tools:', error);
      showMessage('error', 'Failed to load tools');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch('/api/tools/stats', {
        headers: await orchestratorApi.getAuthHeaders()
      });

      const data = await response.json();

      if (data.ok) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to load tool stats:', error);
    }
  };

  const loadExecutions = async () => {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: (page * limit).toString(),
      });

      if (selectedTool) {
        params.append('tool_id', selectedTool.id);
      }

      const response = await fetch(`/api/tools/executions?${params}`, {
        headers: await orchestratorApi.getAuthHeaders()
      });

      const data = await response.json();

      if (data.ok) {
        setExecutions(data.executions);
      }
    } catch (error) {
      console.error('Failed to load executions:', error);
    }
  };

  const handleResetRateLimit = async (toolId: string) => {
    try {
      const response = await fetch(`/api/tools/${toolId}/reset-rate-limit`, {
        method: 'POST',
        headers: await orchestratorApi.getAuthHeaders()
      });

      const data = await response.json();

      if (data.ok) {
        showMessage('success', 'Rate limit reset successfully');
      } else {
        showMessage('error', data.error || 'Failed to reset rate limit');
      }
    } catch (error) {
      console.error('Failed to reset rate limit:', error);
      showMessage('error', 'Failed to reset rate limit');
    }
  };

  const loadAvailability = async () => {
    if (!availabilityEntityId) {
      console.warn('No entity_id available for loading availability');
      return;
    }

    try {
      const params = new URLSearchParams({ entity_id: availabilityEntityId });
      const response = await fetch(`/api/calendar/availability?${params}`, {
        headers: await orchestratorApi.getAuthHeaders()
      });

      const data = await response.json();

      if (data.ok) {
        setAvailability(data.availability || []);
      } else {
        showMessage('error', data.error || 'Failed to load availability settings');
      }
    } catch (error) {
      console.error('Failed to load availability settings:', error);
      showMessage('error', 'Failed to load availability settings');
    }
  };

  const loadEntities = async () => {
    try {
      const response = await fetch('/api/entities', {
        headers: await orchestratorApi.getAuthHeaders()
      });

      const data = await response.json();

      if (data.ok && data.entities) {
        setEntities(data.entities);
        // Auto-select first entity if none selected
        if (!availabilityEntityId && data.entities.length > 0) {
          setAvailabilityEntityId(data.entities[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to load entities:', error);
    }
  };

  const saveAvailability = async () => {
    if (!availabilityEntityId) {
      showMessage('error', 'No entity selected');
      return;
    }

    setSavingAvailability(true);
    try {
      const response = await fetch('/api/calendar/availability', {
        method: 'PUT',
        headers: {
          ...await orchestratorApi.getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          entity_id: availabilityEntityId,
          availability: availability
        })
      });

      const data = await response.json();

      if (data.ok) {
        showMessage('success', 'Availability settings saved successfully');
      } else {
        showMessage('error', data.error || 'Failed to save availability settings');
      }
    } catch (error) {
      console.error('Failed to save availability settings:', error);
      showMessage('error', 'Failed to save availability settings');
    } finally {
      setSavingAvailability(false);
    }
  };

  const updateAvailabilityDay = (dayOfWeek: number, field: 'start_time' | 'end_time' | 'is_enabled', value: string | boolean) => {
    setAvailability(prev => {
      const existing = prev.find(s => s.day_of_week === dayOfWeek);
      if (existing) {
        return prev.map(s => s.day_of_week === dayOfWeek ? { ...s, [field]: value } : s);
      } else {
        return [...prev, {
          day_of_week: dayOfWeek,
          start_time: '09:00',
          end_time: '18:00',
          is_enabled: true,
          [field]: value
        } as AvailabilitySetting];
      }
    });
  };

  const showMessage = (type: 'success' | 'error' | 'info', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  return (
    <div className="google-calendar-auth">
      <div className="gca-header">
        <h2>🔧 Tool Configuration</h2>
        <p className="gca-description">
          Manage AI agent tools that enable agents to perform actions like searching knowledge base,
          booking calendar appointments, managing CRM leads, and escalating to humans.
        </p>
      </div>

      {message && (
        <div className={`gca-message gca-message-${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="gca-tabs" style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        <button
          className={`gca-button gca-button-small ${activeTab === 'tools' ? 'gca-button-primary' : ''}`}
          onClick={() => { setActiveTab('tools'); setSelectedTool(null); setPage(0); }}
        >
          🔧 Tools
        </button>
        <button
          className={`gca-button gca-button-small ${activeTab === 'history' ? 'gca-button-primary' : ''}`}
          onClick={() => { setActiveTab('history'); setPage(0); }}
        >
          📊 Execution History
        </button>
        <button
          className={`gca-button gca-button-small ${activeTab === 'availability' ? 'gca-button-primary' : ''}`}
          onClick={() => { setActiveTab('availability'); setPage(0); }}
        >
          📅 Calendar Availability
        </button>
      </div>

      {activeTab === 'tools' && (
        <>
          {/* Tools Grid */}
          {loading ? (
            <p>Loading tools...</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
              {tools.map((tool) => {
                const toolStats = stats[tool.id];

                return (
                  <div
                    key={tool.id}
                    className="gca-card"
                    style={{
                      borderLeft: `4px solid ${STATUS_COLORS[tool.status]}`,
                      cursor: 'pointer',
                    }}
                    onClick={() => setSelectedTool(tool)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
                      <div>
                        <h3 style={{ margin: 0 }}>{tool.name}</h3>
                        <span
                          style={{
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            backgroundColor: CATEGORY_COLORS[tool.category] + '20',
                            color: CATEGORY_COLORS[tool.category],
                          }}
                        >
                          {tool.category}
                        </span>
                      </div>
                      <span
                        style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          backgroundColor: STATUS_COLORS[tool.status] + '20',
                          color: STATUS_COLORS[tool.status],
                        }}
                      >
                        {STATUS_LABELS[tool.status]}
                      </span>
                    </div>

                    <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '15px' }}>
                      {tool.description}
                    </p>

                    {/* Stats */}
                    {toolStats && toolStats.total_calls > 0 && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '15px' }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{toolStats.total_calls}</div>
                          <div style={{ fontSize: '11px', color: '#6b7280' }}>Calls</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{toolStats.success_rate.toFixed(0)}%</div>
                          <div style={{ fontSize: '11px', color: '#6b7280' }}>Success</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '18px', fontWeight: 'bold' }}>${toolStats.cost_usd.toFixed(4)}</div>
                          <div style={{ fontSize: '11px', color: '#6b7280' }}>Cost</div>
                        </div>
                      </div>
                    )}

                    {/* Tags */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                      {tool.tags.map(tag => (
                        <span
                          key={tag}
                          style={{
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            backgroundColor: '#f3f4f6',
                            color: '#6b7280',
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                      <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#9ca3af' }}>
                        v{tool.version}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {activeTab === 'history' && (
        <>
          {/* Tool Filter */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ marginRight: '10px' }}>Filter by Tool:</label>
            <select
              value={selectedTool?.id || 'all'}
              onChange={(e) => {
                const tool = e.target.value === 'all' ? null : tools.find(t => t.id === e.target.value) || null;
                setSelectedTool(tool);
                setPage(0);
              }}
              style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            >
              <option value="all">All Tools</option>
              {tools.map(tool => (
                <option key={tool.id} value={tool.id}>{tool.name}</option>
              ))}
            </select>
          </div>

          {/* Executions Table */}
          <div className="gca-card">
            <h3>Execution History {selectedTool && `(${selectedTool.name})`}</h3>

            {executions.length === 0 ? (
              <p className="gca-empty">No executions found.</p>
            ) : (
              <table className="gca-table">
                <thead>
                  <tr>
                    <th>Tool</th>
                    <th>Status</th>
                    <th>Duration</th>
                    <th>Cost</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {executions.map((exec) => {
                    const tool = tools.find(t => t.id === exec.tool_id);

                    return (
                      <tr key={exec.id}>
                        <td>
                          <span style={{ color: CATEGORY_COLORS[tool?.category || 'custom'] }}>
                            {tool?.name || exec.tool_id}
                          </span>
                        </td>
                        <td>
                          {exec.success ? (
                            <span className="gca-badge gca-badge-active">Success</span>
                          ) : (
                            <span className="gca-badge" style={{ backgroundColor: '#fecaca', color: '#991b1b' }}>Failed</span>
                          )}
                        </td>
                        <td>{exec.execution_time_ms}ms</td>
                        <td>${exec.cost_usd.toFixed(6)}</td>
                        <td style={{ fontSize: '12px', color: '#6b7280' }}>
                          {new Date(exec.created_at).toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {/* Pagination */}
            {executions.length >= limit && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
                <button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="gca-button gca-button-small"
                >
                  Previous
                </button>
                <span style={{ padding: '10px' }}>Page {page + 1}</span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={executions.length < limit}
                  className="gca-button gca-button-small"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'availability' && (
        <div className="gca-card">
          <div style={{ marginBottom: '20px' }}>
            <h3>📅 Calendar Booking Availability</h3>
            <p style={{ color: '#6b7280', marginTop: '5px' }}>
              Configure which days and times are available for appointment booking.
              Ava will only offer time slots that fall within the configured availability.
            </p>
          </div>

          {/* Entity Selector */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
              Select Entity:
            </label>
            <select
              value={availabilityEntityId}
              onChange={(e) => setAvailabilityEntityId(e.target.value)}
              style={{
                width: '100%',
                maxWidth: '400px',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'white',
              }}
            >
              {entities.length === 0 ? (
                <option value="">No entities available</option>
              ) : (
                entities.map((entity) => (
                  <option key={entity.id} value={entity.id}>
                    {entity.name} ({entity.id})
                  </option>
                ))
              )}
            </select>
          </div>

          {!availabilityEntityId ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
              No entity selected. Please select an entity from the dropdown above.
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '20px' }}>
                {DAY_NAMES.map((dayName, index) => {
                  const daySetting = availability.find(s => s.day_of_week === index);
                  const isEnabled = daySetting?.is_enabled ?? false;

                  return (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '15px',
                        padding: '12px',
                        borderBottom: index < 6 ? '1px solid #e5e7eb' : 'none',
                        backgroundColor: isEnabled ? '#f9fafb' : '#f3f4f6',
                      }}
                    >
                      {/* Enable/Disable Checkbox */}
                      <input
                        type="checkbox"
                        id={`day-${index}`}
                        checked={isEnabled}
                        onChange={(e) => updateAvailabilityDay(index, 'is_enabled', e.target.checked)}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />

                      {/* Day Name */}
                      <label
                        htmlFor={`day-${index}`}
                        style={{
                          minWidth: '100px',
                          fontWeight: isEnabled ? '600' : '400',
                          color: isEnabled ? '#111827' : '#9ca3af',
                          cursor: 'pointer',
                        }}
                      >
                        {dayName}
                      </label>

                      {/* Time Range Inputs */}
                      {isEnabled && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ fontSize: '14px', color: '#6b7280' }}>From:</span>
                          <input
                            type="time"
                            value={daySetting?.start_time || '09:00'}
                            onChange={(e) => updateAvailabilityDay(index, 'start_time', e.target.value)}
                            style={{
                              padding: '6px 10px',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              fontSize: '14px',
                            }}
                          />
                          <span style={{ fontSize: '14px', color: '#6b7280' }}>To:</span>
                          <input
                            type="time"
                            value={daySetting?.end_time || '18:00'}
                            onChange={(e) => updateAvailabilityDay(index, 'end_time', e.target.value)}
                            style={{
                              padding: '6px 10px',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              fontSize: '14px',
                            }}
                          />
                        </div>
                      )}

                      {/* Status Badge */}
                      <div style={{ marginLeft: 'auto' }}>
                        {isEnabled ? (
                          <span style={{
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            backgroundColor: '#d1fae5',
                            color: '#065f46',
                          }}>
                            Available
                          </span>
                        ) : (
                          <span style={{
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            backgroundColor: '#e5e7eb',
                            color: '#6b7280',
                          }}>
                            Disabled
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Save Button */}
              <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={saveAvailability}
                  disabled={savingAvailability}
                  className="gca-button"
                  style={{
                    opacity: savingAvailability ? 0.6 : 1,
                    cursor: savingAvailability ? 'not-allowed' : 'pointer',
                  }}
                >
                  {savingAvailability ? 'Saving...' : 'Save Availability Settings'}
                </button>
              </div>

              {/* Entity Info */}
              <div style={{ marginTop: '15px', fontSize: '12px', color: '#9ca3af' }}>
                Configuring availability for: {entities.find(e => e.id === availabilityEntityId)?.name || availabilityEntityId || 'None selected'}
              </div>
            </>
          )}
        </div>
      )}

      {/* Tool Detail Modal */}
      {selectedTool && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setSelectedTool(null)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '20px',
              maxWidth: '700px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2>{selectedTool.name}</h2>
              <button
                onClick={() => setSelectedTool(null)}
                style={{ border: 'none', background: 'none', fontSize: '24px', cursor: 'pointer' }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <p>{selectedTool.description}</p>
              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <span style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: CATEGORY_COLORS[selectedTool.category] + '20', color: CATEGORY_COLORS[selectedTool.category] }}>
                  {selectedTool.category}
                </span>
                <span style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: STATUS_COLORS[selectedTool.status] + '20', color: STATUS_COLORS[selectedTool.status] }}>
                  {STATUS_LABELS[selectedTool.status]}
                </span>
                <span style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: '#f3f4f6', color: '#6b7280' }}>
                  v{selectedTool.version}
                </span>
              </div>
            </div>

            {/* Stats */}
            {stats[selectedTool.id] && stats[selectedTool.id].total_calls > 0 && (
              <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                <h4>Statistics</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px' }}>
                  <div>
                    <strong>Total Calls:</strong> {stats[selectedTool.id].total_calls}
                  </div>
                  <div>
                    <strong>Success Rate:</strong> {stats[selectedTool.id].success_rate.toFixed(1)}%
                  </div>
                  <div>
                    <strong>Avg Duration:</strong> {stats[selectedTool.id].avg_execution_time_ms.toFixed(0)}ms
                  </div>
                  <div>
                    <strong>Total Cost:</strong> ${stats[selectedTool.id].cost_usd.toFixed(4)}
                  </div>
                </div>
              </div>
            )}

            {/* Parameters */}
            <div style={{ marginBottom: '20px' }}>
              <h4>Parameters</h4>
              {selectedTool.parameters.length === 0 ? (
                <p style={{ color: '#6b7280' }}>No parameters</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <th style={{ textAlign: 'left', padding: '8px' }}>Name</th>
                      <th style={{ textAlign: 'left', padding: '8px' }}>Type</th>
                      <th style={{ textAlign: 'left', padding: '8px' }}>Required</th>
                      <th style={{ textAlign: 'left', padding: '8px' }}>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedTool.parameters.map(param => (
                      <tr key={param.name}>
                        <td style={{ padding: '8px', fontFamily: 'monospace' }}>{param.name}</td>
                        <td style={{ padding: '8px' }}>
                          <span style={{
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            backgroundColor: '#e5e7eb',
                          }}>
                            {param.type}
                          </span>
                        </td>
                        <td style={{ padding: '8px' }}>
                          {param.required ? (
                            <span style={{ color: '#ef4444' }}>Required</span>
                          ) : (
                            <span style={{ color: '#9ca3af' }}>Optional</span>
                          )}
                        </td>
                        <td style={{ padding: '8px' }}>{param.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Rate Limit */}
            {selectedTool.rate_limit && (
              <div style={{ marginBottom: '20px' }}>
                <h4>Rate Limits</h4>
                <div style={{ display: 'flex', gap: '20px' }}>
                  {selectedTool.rate_limit.max_calls_per_minute && (
                    <div>
                      <strong>Per Minute:</strong> {selectedTool.rate_limit.max_calls_per_minute}
                    </div>
                  )}
                  {selectedTool.rate_limit.max_calls_per_hour && (
                    <div>
                      <strong>Per Hour:</strong> {selectedTool.rate_limit.max_calls_per_hour}
                    </div>
                  )}
                  {selectedTool.rate_limit.max_calls_per_day && (
                    <div>
                      <strong>Per Day:</strong> {selectedTool.rate_limit.max_calls_per_day}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => handleResetRateLimit(selectedTool.id)}
                  className="gca-button gca-button-small"
                  style={{ marginTop: '10px' }}
                >
                  Reset Rate Limit
                </button>
              </div>
            )}

            {/* Cost */}
            {selectedTool.cost_per_call_usd !== undefined && (
              <div style={{ marginBottom: '20px' }}>
                <h4>Cost</h4>
                <p><strong>Per Call:</strong> ${selectedTool.cost_per_call_usd.toFixed(6)}</p>
              </div>
            )}

            {/* Tags */}
            <div>
              <h4>Tags</h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                {selectedTool.tags.map(tag => (
                  <span
                    key={tag}
                    style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: '#f3f4f6',
                      color: '#6b7280',
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setSelectedTool(null)}
                className="gca-button"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info Section */}
      <div className="gca-card gca-info">
        <h3>ℹ️ About AI Agent Tools</h3>
        <p className="gca-subtext">
          AI agents use tools to perform actions beyond text generation. Each tool has specific
          parameters, rate limits, and costs. Tools are organized by category and can be enabled/disabled
          based on your needs.
        </p>
        <div className="gca-steps">
          <strong>Available Tools:</strong>
          <ul style={{ marginTop: '10px' }}>
            <li><strong>RAG Search:</strong> Search knowledge base for relevant information</li>
            <li><strong>Calendar Booking:</strong> Book appointments via Google Calendar</li>
            <li><strong>Calendar Check:</strong> Check calendar availability before booking</li>
            <li><strong>CRM Leads:</strong> Create and manage leads in CRM system</li>
            <li><strong>WhatsApp Send:</strong> Send WhatsApp messages to chats</li>
            <li><strong>WhatsApp Get Sender Phone:</strong> Get the phone number of the current chat sender</li>
            <li><strong>Human Handoff:</strong> Transfer conversation to human agent</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ToolConfiguration;
